import {
  RateType,
  TariffGroupType,
  TariffType,
  TariffZoneType,
} from "../../../../generated/graphql";
import type { BulkBinding, BulkRow } from "./bulk-types";

/**
 * Ordered manifest CSV columns. Each binding carries both a human-readable name
 * column and a UUID column where applicable; on load the UUID is authoritative
 * and the contact name is a fallback for hand-added rows.
 */
export const MANIFEST_COLUMNS = [
  "csvFile",
  "tariffName",
  "contactNames",
  "contactUuids",
  "tariffZoneType",
  "tariffType",
  "rateType",
  "tariffGroupType",
  "tariffZoneGroupId",
  "serviceUuids",
  "autoSelectAllServices",
  "vehicleTypeUuids",
  "autoSelectAllVehicleTypes",
  "fuelProfileUuid",
  "startDate",
  "endDate",
  "useActualWeight",
] as const;

const MULTI_SEP = ";";

const joinIds = (ids: string[]): string => ids.join(MULTI_SEP);

const splitIds = (value: string): string[] =>
  value
    .split(MULTI_SEP)
    .map((part) => part.trim())
    .filter((part) => part.length > 0);

/**
 * Parses a string into an enum value, falling back to the shared default.
 *
 * Surrounding whitespace is trimmed first: manifests are hand-edited in a
 * spreadsheet, and `" FLAT "` is a valid intent expressed untidily, not a
 * mistake worth repricing a tariff over.
 *
 * A blank cell then means "inherit the shared default" and is silent. A
 * non-blank value that is still not a member of the enum is a typo or a stale
 * column, and it returns a warning: these cells carry the tariff's rate unit,
 * so silently substituting a default here misprices the tariff rather than
 * merely misconfiguring it.
 *
 * Returns the warning rather than pushing into a caller-owned array, so the
 * caller owns its own collection and there is no shared mutable state.
 *
 * @param field Column name, used in the warning text.
 */
const parseEnum = <T extends Record<string, string>>(
  value: string,
  enumObject: T,
  fallback: T[keyof T],
  field: string,
): { value: T[keyof T]; warning: string | null } => {
  const trimmed = value.trim();
  const members = Object.values(enumObject);
  if (members.includes(trimmed)) {
    return { value: trimmed as T[keyof T], warning: null };
  }
  return {
    value: fallback,
    warning:
      trimmed.length > 0
        ? `${field} "${trimmed}" is not a recognized value; using the shared default "${String(fallback)}"`
        : null,
  };
};

/**
 * Serializes a bulk row to a manifest record, including human-readable contact
 * names resolved from the supplied lookup.
 *
 * @param row The bulk row to serialize.
 * @param contactLabelByUuid Map from contact UUID to display name.
 * @returns A string-keyed manifest record.
 */
export const rowToRecord = (
  row: BulkRow,
  contactLabelByUuid: ReadonlyMap<string, string>,
): Record<string, string> => {
  const { binding } = row;
  return {
    csvFile: row.fileName,
    tariffName: binding.tariffName,
    contactNames: binding.contactUuids
      .map((uuid) => contactLabelByUuid.get(uuid) ?? "")
      .filter((name) => name.length > 0)
      .join(MULTI_SEP),
    contactUuids: joinIds(binding.contactUuids),
    tariffZoneType: binding.tariffZoneType,
    tariffType: binding.tariffType,
    rateType: binding.rateType,
    tariffGroupType: binding.tariffGroupType,
    tariffZoneGroupId: binding.tariffZoneGroupId ?? "",
    serviceUuids: joinIds(binding.serviceUuids),
    autoSelectAllServices: String(binding.autoSelectAllServices),
    vehicleTypeUuids: joinIds(binding.vehicleTypeUuids),
    autoSelectAllVehicleTypes: String(binding.autoSelectAllVehicleTypes),
    fuelProfileUuid: binding.fuelProfileUuid ?? "",
    startDate: binding.startDate ?? "",
    endDate: binding.endDate ?? "",
    useActualWeight: String(binding.useActualWeight),
  };
};

/**
 * Result of resolving one manifest record into a binding, plus any non-fatal
 * warning (e.g. an unresolved contact name) the UI surfaces on the row.
 */
export type RecordResolution = {
  fileName: string;
  binding: BulkBinding;
  warning: string | null;
};

/**
 * Resolves a manifest record into a typed binding. UUID columns are
 * authoritative; when `contactUuids` is blank, `contactNames` are resolved
 * against the supplied lookup. Unknown names and names that match more than one
 * contact (ambiguous) both produce a warning and are skipped, never silently
 * resolved to an arbitrary contact.
 *
 * Enum columns behave the same way: a blank cell inherits the shared default
 * silently, but a non-blank value that is not a member of its enum falls back
 * AND warns, because those columns carry the tariff's rate unit.
 *
 * @param record The manifest record (string-keyed).
 * @param defaults The current shared defaults, used for fields the manifest
 *   leaves blank.
 * @param contactUuidsByLabel Map from contact display name to the list of
 *   contact UUIDs sharing that name (one entry normally; more than one means the
 *   name is ambiguous).
 * @returns The resolved filename, binding, and an optional warning.
 */
export const recordToResolution = (
  record: Record<string, string>,
  defaults: BulkBinding,
  contactUuidsByLabel: ReadonlyMap<string, string[]>,
): RecordResolution => {
  const warnings: string[] = [];

  let contactUuids = splitIds(record.contactUuids ?? "");
  const contactNamesRaw = record.contactNames ?? "";
  if (contactUuids.length === 0 && contactNamesRaw.length > 0) {
    const names = contactNamesRaw
      .split(MULTI_SEP)
      .map((name) => name.trim())
      .filter((name) => name.length > 0);
    const resolved: string[] = [];
    const unresolved: string[] = [];
    const ambiguous: string[] = [];
    for (const name of names) {
      const matches = contactUuidsByLabel.get(name) ?? [];
      const [onlyMatch] = matches;
      if (matches.length === 1 && onlyMatch !== undefined) {
        resolved.push(onlyMatch);
      } else if (matches.length === 0) {
        unresolved.push(name);
      } else {
        ambiguous.push(name);
      }
    }
    contactUuids = resolved;
    if (unresolved.length > 0) {
      warnings.push(`Unresolved contact name(s): ${unresolved.join(", ")}`);
    }
    if (ambiguous.length > 0) {
      warnings.push(
        `Ambiguous contact name(s), specify contactUuid: ${ambiguous.join(", ")}`,
      );
    }
  }

  const tariffZoneGroupId = record.tariffZoneGroupId ?? "";
  const fuelProfileUuid = record.fuelProfileUuid ?? "";
  const startDate = record.startDate ?? "";
  const endDate = record.endDate ?? "";
  const tariffName = record.tariffName ?? "";

  const parsedTariffZoneType = parseEnum(
    record.tariffZoneType ?? "",
    TariffZoneType,
    defaults.tariffZoneType,
    "tariffZoneType",
  );
  const parsedTariffType = parseEnum(
    record.tariffType ?? "",
    TariffType,
    defaults.tariffType,
    "tariffType",
  );
  const parsedRateType = parseEnum(
    record.rateType ?? "",
    RateType,
    defaults.rateType,
    "rateType",
  );
  const parsedTariffGroupType = parseEnum(
    record.tariffGroupType ?? "",
    TariffGroupType,
    defaults.tariffGroupType,
    "tariffGroupType",
  );
  for (const parsed of [
    parsedTariffZoneType,
    parsedTariffType,
    parsedRateType,
    parsedTariffGroupType,
  ]) {
    if (parsed.warning !== null) {
      warnings.push(parsed.warning);
    }
  }

  const binding: BulkBinding = {
    tariffName: tariffName.length > 0 ? tariffName : defaults.tariffName,
    contactUuids,
    serviceUuids: splitIds(record.serviceUuids ?? ""),
    autoSelectAllServices: record.autoSelectAllServices === "true",
    vehicleTypeUuids: splitIds(record.vehicleTypeUuids ?? ""),
    autoSelectAllVehicleTypes: record.autoSelectAllVehicleTypes === "true",
    tariffZoneType: parsedTariffZoneType.value,
    tariffType: parsedTariffType.value,
    rateType: parsedRateType.value,
    tariffGroupType: parsedTariffGroupType.value,
    tariffZoneGroupId: tariffZoneGroupId.length > 0 ? tariffZoneGroupId : null,
    fuelProfileUuid: fuelProfileUuid.length > 0 ? fuelProfileUuid : null,
    startDate: startDate.length > 0 ? startDate : null,
    endDate: endDate.length > 0 ? endDate : null,
    useActualWeight: record.useActualWeight === "true",
  };

  const warning = warnings.length > 0 ? warnings.join("; ") : null;
  return { fileName: record.csvFile ?? "", binding, warning };
};
